<script setup lang="ts">
/**
 * The first screen anybody sees, and the first one in this product that asks a
 * human for a secret.
 *
 * Deliberately one form on an otherwise empty page. A back office that shows
 * its navigation behind a sign-in box is advertising a shape somebody has not
 * been admitted to, and every one of those panels would answer 401 anyway.
 */

import { computed, ref } from "vue";
import type { SignInFailure } from "../session";

const props = defineProps<{
  /**
   * A BARE FUNCTION, which is the console's convention — not the till's
   * `Translator` object with a `.t()` method.
   *
   * Worth the comment because the first version of this file used the other
   * one, typed the prop as `Translator`, and rendered a completely blank page:
   * `h()` types its props loosely enough that `tsc` was happy, and the failure
   * was `$props.t.t is not a function` in a browser nobody was watching. Every
   * other component in this directory takes `(key: string) => string`.
   */
  t: (key: string) => string;
  venueName: string | null;
  busy: boolean;
  failure: SignInFailure | null;
}>();

const emit = defineEmits<{ submit: [staffId: string, pin: string] }>();

const staffId = ref("");
const pin = ref("");

/** Nothing to submit until both are filled, so the button cannot look broken. */
const ready = computed(() => staffId.value.trim().length > 0 && pin.value.length > 0);

const message = computed(() => {
  const failure = props.failure;
  if (!failure) return null;
  if (failure.reason === "UNREACHABLE") return props.t("signIn.unreachable");
  if (failure.reason === "NOT_BACK_OFFICE") return props.t("signIn.notBackOffice");
  if (failure.reason === "TOO_MANY_ATTEMPTS") {
    return props.t("signIn.tooMany").replace("{minutes}", String(failure.retryAfterMinutes));
  }
  return props.t("signIn.badCredentials");
});

function submit(): void {
  if (!ready.value || props.busy) return;
  emit("submit", staffId.value.trim(), pin.value);
  /**
   * THE PIN IS CLEARED ON EVERY ATTEMPT, right or wrong.
   *
   * On success the field is about to be unmounted anyway. On failure, leaving
   * six digits sitting in a form on a screen in a restaurant office is the
   * thing worth avoiding — and re-typing is exactly the cost that makes a
   * repeated guess expensive.
   */
  pin.value = "";
}
</script>

<template>
  <main class="signin">
    <form class="signin__card" @submit.prevent="submit">
      <!--
        WHOSE OFFICE THIS IS, before anything is asked. The card used to open
        on a bare "Přihlášení": no mark, no product, and on a shared laptop no
        way to tell this venue's office from a phishing page's.
      -->
      <div class="signin__brand">
        <img src="/icon.svg" alt="" width="32" height="32" />
        <div class="signin__brandText">
          <strong class="signin__product">GastroFirst</strong>
          <span v-if="venueName" class="signin__venue">{{ venueName }}</span>
        </div>
      </div>

      <h1 class="signin__title">{{ t("signIn.title") }}</h1>
      <p class="signin__subtitle">{{ t("signIn.subtitle") }}</p>

      <label class="signin__label" for="signin-staff">{{ t("signIn.staff") }}</label>
      <!--
        The explanation is a HINT under the label, not the placeholder: a
        placeholder vanishes on the first keystroke and was cut off at the
        field's edge ("…např. Eva S" with the full stop gone).
      -->
      <p id="signin-staff-hint" class="signin__hint">{{ t("signIn.staffHint") }}</p>
      <input
        aria-describedby="signin-staff-hint"
        id="signin-staff"
        v-model="staffId"
        class="signin__input"
        type="text"
        name="username"
        autocomplete="username"
        autocapitalize="words"
        spellcheck="false"
        :placeholder="t('signIn.staffPlaceholder')"
        :disabled="busy"
      />

      <label class="signin__label" for="signin-pin">{{ t("signIn.pin") }}</label>
      <!--
        `inputmode="numeric"` rather than `type="number"`: a number input strips
        leading zeros, and a PIN of 012345 is not 12345.
      -->
      <input
        id="signin-pin"
        v-model="pin"
        class="signin__input signin__input--pin"
        type="password"
        inputmode="numeric"
        name="current-password"
        autocomplete="current-password"
        :disabled="busy"
      />

      <!--
        `role="alert"`, so a screen reader announces the refusal rather than
        leaving somebody to wonder whether the button did anything. The EU
        Accessibility Act is a legal gate here, not a preference.
      -->
      <p v-if="message" class="signin__error" role="alert">{{ message }}</p>

      <button class="signin__submit" type="submit" :disabled="!ready || busy">
        {{ busy ? t("signIn.working") : t("signIn.submit") }}
      </button>
    </form>
  </main>
</template>

<style scoped>
/* The sign-in floats: MyMonday's floating card on the darker frame colour. */
.signin {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: var(--gf-space-5);
  background: var(--gf-color-canvas);
}

.signin__card {
  width: min(24rem, 100%);
  display: flex;
  flex-direction: column;
  padding: var(--gf-space-7) var(--gf-space-6) var(--gf-space-6);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-xl);
  background: var(--gf-color-surface);
  box-shadow: var(--gf-shadow-md);
}

.signin__brand {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  margin-bottom: var(--gf-space-6);
}

.signin__brand img {
  flex: none;
  border-radius: var(--gf-radius-md);
}

.signin__brandText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: var(--gf-leading-tight);
}

.signin__product {
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
}

.signin__hint {
  margin: 0 0 var(--gf-space-2);
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

.signin__title {
  margin: 0;
  font-size: var(--gf-text-xl);
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
}

.signin__venue {
  overflow: hidden;
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.signin__subtitle {
  margin: var(--gf-space-4) 0 var(--gf-space-5);
  color: var(--gf-color-text-muted);
  line-height: var(--gf-leading-normal);
}

.signin__label {
  margin-bottom: var(--gf-space-1);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

.signin__input {
  margin-bottom: var(--gf-space-4);
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-3);
  font: inherit;
  color: var(--gf-color-text);
  background: var(--gf-color-surface);
  border: var(--gf-border-thin) solid var(--gf-color-border);
  border-radius: var(--gf-radius-md);
}

.signin__input--pin {
  /* Wide tracking so six identical dots are countable at a glance. */
  letter-spacing: 0.35em;
}

.signin__input:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.signin__error {
  margin: 0 0 var(--gf-space-4);
  padding: var(--gf-space-3) var(--gf-space-4);
  color: var(--gf-color-text);
  background: var(--gf-color-danger-surface);
  border-radius: var(--gf-radius-md);
  line-height: var(--gf-leading-normal);
}

/* The one thing this screen is for, so the one primary fill. */
.signin__submit {
  min-height: var(--gf-target-min);
  font: inherit;
  font-weight: var(--gf-weight-semibold);
  color: var(--gf-color-on-primary);
  background: var(--gf-color-primary);
  border: var(--gf-border-thin) solid var(--gf-color-primary);
  border-radius: var(--gf-radius-md);
  box-shadow: var(--gf-shadow-glow);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .signin__submit:hover:not(:disabled) {
    background: color-mix(in srgb, var(--gf-color-primary) 88%, var(--gf-color-text));
  }
}

.signin__submit:disabled {
  opacity: 0.5;
  cursor: default;
  box-shadow: none;
}

.signin__submit:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

/*
 * A primary that cannot be pressed yet is NEUTRAL, not a faded red: a washed-out
 * oxblood still reads as "the button to press", and pink is not a state.
 */
.signin__submit:disabled {
  color: var(--gf-color-text-muted);
  background: var(--gf-color-surface-active);
  border-color: transparent;
  box-shadow: none;
  opacity: 1;
}
</style>
